import { useState, useEffect } from "react";
import { api, useAuth } from "@/App";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  User,
  Bell,
  Shield,
  Loader2,
  Save,
  Clock,
  Tag,
  MapPin,
  Plus,
  Edit,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

function AccessRestricted() {
  const { t } = useI18n();
  return (
    <div className="max-w-2xl mx-auto mt-16 text-center space-y-4">
      <h1 className="font-heading font-bold text-2xl text-foreground">{t("settings.accessRestricted")}</h1>
      <p className="text-muted-foreground">
        {t("settings.accessRestrictedDesc")}
      </p>
    </div>
  );
}

function AdminSettingsContent({ user }) {
  const { t } = useI18n();
  const [loading, setLoading] = useState(false);

  // Categories state
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [categoryName, setCategoryName] = useState("");
  const [deleteCategoryId, setDeleteCategoryId] = useState(null);
  const [submittingCategory, setSubmittingCategory] = useState(false);

  // Storage locations state
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationName, setLocationName] = useState("");
  const [deleteLocationId, setDeleteLocationId] = useState(null);
  const [submittingLocation, setSubmittingLocation] = useState(false);

  // Reassignment state
  const [reassignCategoryId, setReassignCategoryId] = useState("");
  const [reassignLocationId, setReassignLocationId] = useState("");
  const [reassigningCategory, setReassigningCategory] = useState(false);
  const [reassigningLocation, setReassigningLocation] = useState(false);

  // Notification prefs (local only for now)
  const [notifications, setNotifications] = useState({
    inAppExpiring: true,
    inAppExpired: true,
    inAppTemperature: true,
    reminderDays: 3,
  });

  useEffect(() => {
    fetchCategories();
    fetchLocations();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await api.get("/categories");
      setCategories(response.data);
    } catch (error) {
      toast.error("Failed to load categories");
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await api.get("/storage-locations");
      setLocations(response.data);
    } catch (error) {
      toast.error("Failed to load storage locations");
    } finally {
      setLoadingLocations(false);
    }
  };

  // Category handlers
  const openCategoryModal = (category = null) => {
    setEditingCategory(category);
    setCategoryName(category ? category.name : "");
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async () => {
    if (!categoryName.trim()) {
      toast.error("Please enter a category name");
      return;
    }

    setSubmittingCategory(true);
    try {
      if (editingCategory) {
        await api.put(`/categories/${editingCategory.category_id}`, { name: categoryName });
        toast.success("Category updated");
      } else {
        await api.post("/categories", { name: categoryName });
        toast.success("Category added");
      }
      fetchCategories();
      setShowCategoryModal(false);
      setCategoryName("");
      setEditingCategory(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save category");
    } finally {
      setSubmittingCategory(false);
    }
  };

  const handleDeleteCategory = async () => {
    if (!deleteCategoryId || !reassignCategoryId) {
      toast.error("Please select a category to reassign products to");
      return;
    }

    setReassigningCategory(true);
    try {
      await api.delete(`/categories/${deleteCategoryId}`, {
        data: { reassign_to_category_id: reassignCategoryId },
      });
      toast.success("Category deleted and products reassigned");
      fetchCategories();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete category");
    } finally {
      setReassigningCategory(false);
      setDeleteCategoryId(null);
      setReassignCategoryId("");
    }
  };

  // Location handlers
  const openLocationModal = (location = null) => {
    setEditingLocation(location);
    setLocationName(location ? location.name : "");
    setShowLocationModal(true);
  };

  const handleSaveLocation = async () => {
    if (!locationName.trim()) {
      toast.error("Please enter a location name");
      return;
    }

    setSubmittingLocation(true);
    try {
      if (editingLocation) {
        await api.put(`/storage-locations/${editingLocation.location_id}`, { name: locationName });
        toast.success("Storage location updated");
      } else {
        await api.post("/storage-locations", { name: locationName });
        toast.success("Storage location added");
      }
      fetchLocations();
      setShowLocationModal(false);
      setLocationName("");
      setEditingLocation(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save storage location");
    } finally {
      setSubmittingLocation(false);
    }
  };

  const handleDeleteLocation = async () => {
    if (!deleteLocationId || !reassignLocationId) {
      toast.error("Please select a storage location to reassign products to");
      return;
    }

    setReassigningLocation(true);
    try {
      await api.delete(`/storage-locations/${deleteLocationId}`, {
        data: { reassign_to_location_id: reassignLocationId },
      });
      toast.success("Storage location deleted and products reassigned");
      fetchLocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete storage location");
    } finally {
      setReassigningLocation(false);
      setDeleteLocationId(null);
      setReassignLocationId("");
    }
  };

  const handleSaveNotifications = async () => {
    setLoading(true);
    try {
      // Placeholder for future persistence of notification prefs
      await new Promise((resolve) => setTimeout(resolve, 500));
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="settings-page" className="max-w-4xl mx-auto space-y-4 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="font-heading font-bold text-2xl text-foreground tracking-tight">{t("settings.title")}</h1>
        <p className="text-sm text-muted-foreground mt-0.5">{t("settings.subtitle")}</p>
      </div>

      {/* Profile Section */}
      <Card className="">
        <CardHeader>
          <div className="flex items-center gap-3">
            <User className="h-5 w-5 text-muted-foreground" />
            <CardTitle className="font-heading text-lg">{t("settings.profileTitle")}</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
              {user?.picture ? (
                <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full" />
              ) : (
                <User className="h-8 w-8 text-primary" />
              )}
            </div>
            <div>
              <p className="font-semibold text-lg">{user?.name || "User"}</p>
              <p className="text-muted-foreground">{user?.email}</p>
              <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded">
                {user?.role || "Team Member"}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Categories Management */}
      <Card className="">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Tag className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle className="font-heading text-lg">{t("settings.categoriesTitle")}</CardTitle>
                <CardDescription>{t("settings.categoriesDesc")}</CardDescription>
              </div>
            </div>
            <Button onClick={() => openCategoryModal()} className="" data-testid="add-category-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t("settings.addCategory")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingCategories ? (
            <div className="text-center py-4 text-muted-foreground">Loading categories...</div>
          ) : (
            <div className="space-y-2">
              {categories.map((category) => (
                <div
                  key={category.category_id}
                  className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-amber-100 flex items-center justify-center">
                      <Tag className="h-4 w-4 text-amber-600" />
                    </div>
                    <span className="font-medium">{category.name}</span>
                    {category.is_default && (
                      <span className="text-xs px-2 py-0.5 bg-slate-200 text-slate-600 rounded">{t("settings.default")}</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openCategoryModal(category)}
                      data-testid={`edit-category-${category.category_id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      <span>Edit</span>
                    </Button>
                    {!category.is_default && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteCategoryId(category.category_id)}
                        data-testid={`delete-category-${category.category_id}`}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        <span>{t("common.delete")}</span>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Storage Locations Management */}
      <Card className="">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <MapPin className="h-5 w-5 text-muted-foreground" />
              <div>
                <CardTitle className="font-heading text-lg">{t("settings.locationsTitle")}</CardTitle>
                <CardDescription>{t("settings.locationsDesc")}</CardDescription>
              </div>
            </div>
            <Button onClick={() => openLocationModal()} className="" data-testid="add-location-btn">
              <Plus className="h-4 w-4 mr-2" />
              {t("settings.addLocation")}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingLocations ? (
            <div className="text-center py-4 text-muted-foreground">Loading locations...</div>
          ) : (
            <div className="space-y-2">
              {locations.map((location) => (
                <div
                  key={location.location_id}
                  className="flex items-center justify-between p-3 bg-zinc-50 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-blue-100 flex items-center justify-center">
                      <MapPin className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="font-medium">{location.name}</span>
                    {location.is_default && (
                      <span className="text-xs px-2 py-0.5 bg-slate-200 text-slate-600 rounded">{t("settings.default")}</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openLocationModal(location)}
                      data-testid={`edit-location-${location.location_id}`}
                    >
                      <Edit className="h-4 w-4 mr-1" />
                      <span>Edit</span>
                    </Button>
                    {!location.is_default && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setDeleteLocationId(location.location_id)}
                        data-testid={`delete-location-${location.location_id}`}
                        className="border-red-200 text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4 mr-1" />
                        <span>{t("common.delete")}</span>
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Notification Settings */}
      <Card className="">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Bell className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle className="font-heading text-lg">{t("settings.notificationsTitle")}</CardTitle>
              <CardDescription>Configure how you receive alerts</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <Bell className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">In-App Notifications</span>
            </div>
            <div className="space-y-4 pl-6">
              <div className="flex items-center justify-between">
                <Label htmlFor="app-expiring" className="cursor-pointer">
                  Products expiring soon
                </Label>
                <Switch
                  id="app-expiring"
                  checked={notifications.inAppExpiring}
                  onCheckedChange={(checked) =>
                    setNotifications((prev) => ({ ...prev, inAppExpiring: checked }))
                  }
                  data-testid="app-expiring-switch"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="app-expired" className="cursor-pointer">
                  Products expired
                </Label>
                <Switch
                  id="app-expired"
                  checked={notifications.inAppExpired}
                  onCheckedChange={(checked) =>
                    setNotifications((prev) => ({ ...prev, inAppExpired: checked }))
                  }
                  data-testid="app-expired-switch"
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="app-temp" className="cursor-pointer">
                  Temperature alerts
                </Label>
                <Switch
                  id="app-temp"
                  checked={notifications.inAppTemperature}
                  onCheckedChange={(checked) =>
                    setNotifications((prev) => ({ ...prev, inAppTemperature: checked }))
                  }
                  data-testid="app-temp-switch"
                />
              </div>
            </div>
          </div>

          <Separator />

          <div>
            <div className="flex items-center gap-2 mb-4">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">Reminder Timing</span>
            </div>
            <div className="pl-6">
              <div className="flex items-center gap-4">
                <Label htmlFor="reminder-days">Days before expiration to notify:</Label>
                <Input
                  id="reminder-days"
                  type="number"
                  min="1"
                  max="30"
                  className="w-20"
                  value={notifications.reminderDays}
                  onChange={(e) =>
                    setNotifications((prev) => ({
                      ...prev,
                      reminderDays: parseInt(e.target.value, 10) || 3,
                    }))
                  }
                  data-testid="reminder-days-input"
                />
              </div>
            </div>
          </div>

          <div className="pt-4">
            <Button
              onClick={handleSaveNotifications}
              disabled={loading}
              className=""
              data-testid="save-settings-btn"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              {loading ? "Saving..." : "Save Settings"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Security Section */}
      <Card className="">
        <CardHeader>
          <div className="flex items-center gap-3">
            <Shield className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle className="font-heading text-lg">Security</CardTitle>
              <CardDescription>Account security settings</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            {t("settings.profileDesc")}
          </p>
        </CardContent>
      </Card>

      {/* Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingCategory ? t("common.edit") : t("settings.addCategory")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Category Name</Label>
              <Input
                placeholder={t("settings.categoryPlaceholder")}
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                data-testid="category-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveCategory}
              disabled={submittingCategory}
              className=""
              data-testid="save-category-btn"
            >
              {submittingCategory ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {submittingCategory ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Location Modal */}
      <Dialog open={showLocationModal} onOpenChange={setShowLocationModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading">
              {editingLocation ? t("common.edit") : t("settings.addLocation")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Location Name</Label>
              <Input
                placeholder={t("settings.locationPlaceholder")}
                value={locationName}
                onChange={(e) => setLocationName(e.target.value)}
                data-testid="location-name-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLocationModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveLocation}
              disabled={submittingLocation}
              className=""
              data-testid="save-location-btn"
            >
              {submittingLocation ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              {submittingLocation ? t("common.saving") : t("common.save")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Category Confirmation & Reassignment */}
      <AlertDialog
        open={!!deleteCategoryId}
        onOpenChange={() => {
          setDeleteCategoryId(null);
          setReassignCategoryId("");
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Choose a category to move all products into before deleting this one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 mt-2">
            <Label htmlFor="reassign-category">Reassign products to</Label>
            <Select value={reassignCategoryId} onValueChange={setReassignCategoryId}>
              <SelectTrigger id="reassign-category" data-testid="reassign-category-select">
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {categories
                  .filter((c) => c.category_id !== deleteCategoryId)
                  .map((c) => (
                    <SelectItem key={c.category_id} value={c.category_id}>
                      {c.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteCategory}
              className="bg-red-600 hover:bg-red-700"
              disabled={reassigningCategory}
            >
              {reassigningCategory ? "Reassigning..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Location Confirmation & Reassignment */}
      <AlertDialog
        open={!!deleteLocationId}
        onOpenChange={() => {
          setDeleteLocationId(null);
          setReassignLocationId("");
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Storage Location</AlertDialogTitle>
            <AlertDialogDescription>
              Choose a storage location to move all products into before deleting this one.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-3 mt-2">
            <Label htmlFor="reassign-location">Reassign products to</Label>
            <Select value={reassignLocationId} onValueChange={setReassignLocationId}>
              <SelectTrigger id="reassign-location" data-testid="reassign-location-select">
                <SelectValue placeholder="Select storage location" />
              </SelectTrigger>
              <SelectContent>
                {locations
                  .filter((l) => l.location_id !== deleteLocationId)
                  .map((l) => (
                    <SelectItem key={l.location_id} value={l.location_id}>
                      {l.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <AlertDialogFooter className="mt-4">
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteLocation}
              className="bg-red-600 hover:bg-red-700"
              disabled={reassigningLocation}
            >
              {reassigningLocation ? "Reassigning..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();

  if (!user || user.role !== "admin") {
    return <AccessRestricted />;
  }

  return <AdminSettingsContent user={user} />;
}
